import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { GET_ALL_GISTS } from "../../services/graphql/queriesMutations";
import { useGlobalContext } from "../../components/context/AuthContext";
import styles from "./DesignLabPage.module.css";

type LabUser = {
  id?: number;
  name?: string | null;
  image?: string | null;
};

type LabComment = {
  id: number;
  comment: string;
  parentId: number | null;
  gistId?: number | null;
  editId?: number | null;
  user?: LabUser | null;
  createdAt?: string | null;
  children: LabComment[];
};

type LabEdit = {
  id: number;
  body?: string | null;
  createdAt?: string | null;
  comments?: Array<Omit<LabComment, "children">> | null;
};

type LabVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: LabEdit[] | null;
  user?: LabUser | null;
};

type LabGist = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  createdAt?: string | null;
  versions?: LabVersion[] | null;
};

type LabGistsQuery = {
  gists: LabGist[];
};

type Lane = {
  depth: number;
  title: string;
  subtitle: string;
  comments: LabComment[];
  selectedId: number | null;
};

type ResizeState = {
  laneIndex: number;
  startX: number;
  startWidth: number;
};

const WIDTH_STORAGE_KEY = "gistoscope.designLab.commentLaneWidths.v1";
const DEFAULT_WIDTHS = [360, 340, 520, 360, 360, 360, 360];
const MIN_LANE_WIDTH = 170;

function stripHtml(input?: string | null): string {
  if (!input) return "";
  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const doc = new window.DOMParser().parseFromString(input, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function excerpt(input?: string | null, limit = 180): string {
  const text = stripHtml(input);
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}…`;
}

function formatAge(date?: string | null): string {
  if (!date) return "unknown time";
  const parsed = new Date(date).getTime();
  if (Number.isNaN(parsed)) return "unknown time";
  const diffMs = Date.now() - parsed;
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 45) return `${days} days ago`;
  return new Date(parsed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLatestEdit(gist: LabGist | null): LabEdit | null {
  if (!gist?.versions?.length) return null;
  const allEdits = gist.versions.flatMap((version) => version.edits || []);
  if (!allEdits.length) return null;
  return [...allEdits].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getAllCommentsForGist(gist: LabGist | null): LabComment[] {
  if (!gist?.versions?.length) return [];
  const unique = new Map<number, LabComment>();

  gist.versions.forEach((version) => {
    (version.edits || []).forEach((edit) => {
      (edit.comments || []).forEach((comment) => {
        if (!unique.has(comment.id)) {
          unique.set(comment.id, {
            ...comment,
            editId: comment.editId ?? edit.id,
            gistId: comment.gistId ?? gist.id,
            children: [],
          });
        }
      });
    });
  });

  const comments: LabComment[] = [];
  unique.forEach((comment) => {
    comments.push(comment);
  });

  return comments.sort(
    (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
  );
}

function buildCommentTree(flatComments: LabComment[]): LabComment[] {
  const byId = new Map<number, LabComment>();
  flatComments.forEach((comment) => {
    byId.set(comment.id, { ...comment, children: [] });
  });

  const roots: LabComment[] = [];
  byId.forEach((comment) => {
    if (comment.parentId && byId.has(comment.parentId)) {
      byId.get(comment.parentId)?.children.push(comment);
    } else {
      roots.push(comment);
    }
  });

  const sortTree = (nodes: LabComment[]) => {
    nodes.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
    nodes.forEach((node) => sortTree(node.children));
  };
  sortTree(roots);

  return roots;
}

function flattenTree(nodes: LabComment[]): LabComment[] {
  return nodes.flatMap((node) => [node, ...flattenTree(node.children)]);
}

function countDescendants(comment: LabComment): number {
  return comment.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

function getScopeLabel(comment: LabComment, currentEditId?: number | null): string {
  if (!comment.editId) return "Gist-wide";
  if (currentEditId && comment.editId === currentEditId) return "Current edit";
  return `Edit ${comment.editId}`;
}

function getScopeClass(comment: LabComment, currentEditId?: number | null): string {
  if (!comment.editId) return styles.scopeGist;
  if (currentEditId && comment.editId === currentEditId) return styles.scopeCurrent;
  return styles.scopeOlder;
}

function readStoredWidths(): number[] {
  if (typeof window === "undefined") return DEFAULT_WIDTHS;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(WIDTH_STORAGE_KEY) || "[]");
    if (Array.isArray(parsed) && parsed.every((value) => typeof value === "number")) {
      return [...DEFAULT_WIDTHS].map((fallback, index) => parsed[index] || fallback);
    }
  } catch {
    // Ignore broken localStorage values.
  }
  return DEFAULT_WIDTHS;
}

const DesignLabPage: React.FC = () => {
  const { user } = useGlobalContext();
  const { data, loading, error } = useQuery<LabGistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const [selectedGistId, setSelectedGistId] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<number[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const [laneWidths, setLaneWidths] = useState<number[]>(readStoredWidths);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [labMode, setLabMode] = useState<"lanes" | "cards">("lanes");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const gists = useMemo(() => data?.gists || [], [data]);

  const rankedGists = useMemo(() => {
    return [...gists].sort((a, b) => getAllCommentsForGist(b).length - getAllCommentsForGist(a).length);
  }, [gists]);

  useEffect(() => {
    if (!selectedGistId && rankedGists.length > 0) {
      setSelectedGistId(rankedGists[0].id);
    }
  }, [rankedGists, selectedGistId]);

  const selectedGist = useMemo(
    () => gists.find((gist) => gist.id === selectedGistId) || rankedGists[0] || null,
    [gists, rankedGists, selectedGistId]
  );

  const latestEdit = useMemo(() => getLatestEdit(selectedGist), [selectedGist]);
  const flatComments = useMemo(() => getAllCommentsForGist(selectedGist), [selectedGist]);
  const roots = useMemo(() => buildCommentTree(flatComments), [flatComments]);
  const allCommentNodes = useMemo(() => flattenTree(roots), [roots]);
  const allById = useMemo(() => new Map(allCommentNodes.map((comment) => [comment.id, comment])), [allCommentNodes]);

  useEffect(() => {
    setSelectedPath([]);
    setExpandedIds(new Set());
  }, [selectedGistId]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, JSON.stringify(laneWidths));
    }
  }, [laneWidths]);

  const visibleMatch = useCallback(
    (comment: LabComment) => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;
      return `${comment.comment} ${comment.user?.name || ""}`.toLowerCase().includes(query);
    },
    [searchQuery]
  );

  const selectedComment = selectedPath.length ? allById.get(selectedPath[selectedPath.length - 1]) || null : null;
  const activeLaneIndex = Math.max(0, selectedPath.length - 1);

  const lanes: Lane[] = useMemo(() => {
    const result: Lane[] = [
      {
        depth: 0,
        title: "Lane 0 · direct comments",
        subtitle: "Comments directly under this Gist",
        comments: roots,
        selectedId: selectedPath[0] ?? null,
      },
    ];

    selectedPath.forEach((commentId, index) => {
      const node = allById.get(commentId);
      if (node?.children.length) {
        result.push({
          depth: index + 1,
          title: `Lane ${index + 1} · replies`,
          subtitle: `Replies to: ${excerpt(node.comment, 64) || "selected comment"}`,
          comments: node.children,
          selectedId: selectedPath[index + 1] ?? null,
        });
      }
    });

    return result;
  }, [allById, roots, selectedPath]);

  const maxLaneWidth = useCallback(() => {
    const visibleWidth = scrollRef.current?.clientWidth || 1100;
    return Math.max(340, visibleWidth - 36);
  }, []);

  const setLaneWidthAt = useCallback(
    (laneIndex: number, width: number) => {
      const maxWidth = maxLaneWidth();
      setLaneWidths((current) => {
        const next = [...current];
        next[laneIndex] = Math.max(MIN_LANE_WIDTH, Math.min(maxWidth, width));
        return next;
      });
    },
    [maxLaneWidth]
  );

  const handleStartResize = (laneIndex: number, event: React.MouseEvent<HTMLButtonElement>) => {
    setResizeState({ laneIndex, startX: event.clientX, startWidth: laneWidths[laneIndex] || DEFAULT_WIDTHS[laneIndex] || 340 });
    event.preventDefault();
  };

  useEffect(() => {
    if (!resizeState) return undefined;

    const handleMove = (event: MouseEvent) => {
      const delta = event.clientX - resizeState.startX;
      setLaneWidthAt(resizeState.laneIndex, resizeState.startWidth + delta);
    };

    const handleUp = () => setResizeState(null);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    document.body.classList.add(styles.bodyResizing);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.classList.remove(styles.bodyResizing);
    };
  }, [resizeState, setLaneWidthAt]);

  const selectComment = (comment: LabComment, depth: number) => {
    setSelectedPath((current) => [...current.slice(0, depth), comment.id]);
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(comment.id)) {
        next.delete(comment.id);
      } else {
        next.add(comment.id);
      }
      return next;
    });
  };

  const focusLane = (laneIndex: number) => {
    const lane = document.getElementById(`design-lab-lane-${laneIndex}`);
    if (lane && scrollRef.current) {
      scrollRef.current.scrollTo({ left: Math.max(0, lane.offsetLeft - 24), behavior: "smooth" });
    }
  };

  const fitSelectedLane = () => {
    setLaneWidthAt(activeLaneIndex, maxLaneWidth());
    window.setTimeout(() => focusLane(activeLaneIndex), 0);
  };

  const compactLanes = () => setLaneWidths([240, 240, 260, 240, 240, 240, 240]);
  const resetWidths = () => setLaneWidths(DEFAULT_WIDTHS);

  const visibleCommentsCount = flatComments.filter(visibleMatch).length;
  const isAdmin = user?.userType === "admin";

  return (
    <main className={styles.page} aria-label="Gistoscope Design Lab">
      <section className={styles.shell}>
        <header className={styles.header}>
          <div>
            <div className={styles.eyebrow}>Read-only sandbox</div>
            <h1>Design Lab</h1>
            <p>
              A removable presentation lab for experimenting with Gists, comments, lanes, and discussion layouts
              using real data without changing the production Reader or Discussion pages.
            </p>
          </div>
          <div className={styles.headerActions}>
            <Link to="/" className={styles.secondaryLink}>Back to Subjects</Link>
            <span className={isAdmin ? styles.adminBadge : styles.warningBadge}>
              {isAdmin ? "Admin lab" : "Read-only preview"}
            </span>
          </div>
        </header>

        <section className={styles.safetyPanel}>
          <strong>Safety rule:</strong> this page only reads existing Gist data. It does not create comments, edit Gists,
          touch Prisma, or change production components. To remove it later, delete the design-lab folder and remove one route/menu link.
        </section>

        <section className={styles.controls} aria-label="Design Lab controls">
          <label className={styles.selectLabel}>
            Data source
            <select
              value={selectedGist?.id ?? ""}
              onChange={(event) => setSelectedGistId(Number(event.target.value))}
              className={styles.select}
            >
              {rankedGists.map((gist) => (
                <option key={gist.id} value={gist.id}>
                  {gist.title} · {getAllCommentsForGist(gist).length} comments
                </option>
              ))}
            </select>
          </label>

          <div className={styles.modeSwitch} aria-label="Lab mode">
            <button
              type="button"
              className={labMode === "lanes" ? styles.activeModeButton : styles.modeButton}
              onClick={() => setLabMode("lanes")}
            >
              Resizable lanes
            </button>
            <button
              type="button"
              className={labMode === "cards" ? styles.activeModeButton : styles.modeButton}
              onClick={() => setLabMode("cards")}
            >
              Gist cards
            </button>
          </div>

          <input
            className={styles.search}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search comments or authors..."
          />
        </section>

        {loading && <div className={styles.message}>Loading real Gist data…</div>}
        {error && <div className={styles.message}>Could not load data. The lab is read-only; refresh after the server is available.</div>}

        {selectedGist && !loading && labMode === "lanes" && (
          <section className={styles.labPanel} aria-label="Resizable comment lanes experiment">
            <div className={styles.panelHeader}>
              <div>
                <div className={styles.eyebrow}>Experiment A</div>
                <h2>Resizable Comment Lanes</h2>
                <p>
                  Each vertical lane is one comment-tree level. Drag dividers to make any lane nearly full width;
                  other lanes move off-screen and remain reachable through horizontal scroll.
                </p>
              </div>
              <div className={styles.stats}>
                <span>{lanes.length} lanes</span>
                <span>{visibleCommentsCount} visible comments</span>
              </div>
            </div>

            <div className={styles.laneToolbar}>
              <button type="button" onClick={fitSelectedLane} className={styles.primaryButton}>Fit selected lane</button>
              <button type="button" onClick={compactLanes} className={styles.toolButton}>Compact lanes</button>
              <button type="button" onClick={resetWidths} className={styles.toolButton}>Reset widths</button>
              <button type="button" onClick={() => focusLane(0)} className={styles.toolButton}>Focus lane 0</button>
              <button type="button" onClick={() => focusLane(activeLaneIndex)} className={styles.toolButton}>
                Focus selected lane
              </button>
            </div>

            <div className={styles.gistPreview}>
              <div className={styles.gistTitle}>{selectedGist.title}</div>
              <div className={styles.gistBody}>{excerpt(latestEdit?.body, 260) || "This Gist has no body text in the selected dataset."}</div>
            </div>

            <div className={styles.pathBar}>
              <span className={styles.pathCrumb}>Gist</span>
              {selectedPath.map((commentId, index) => {
                const comment = allById.get(commentId);
                return (
                  <span key={commentId} className={styles.pathCrumbActive}>
                    L{index}: {excerpt(comment?.comment, 48)}
                  </span>
                );
              })}
            </div>

            <div className={styles.lanesFrame} ref={scrollRef}>
              <div className={styles.lanesCanvas}>
                {lanes.map((lane, index) => (
                  <React.Fragment key={lane.depth}>
                    <section
                      id={`design-lab-lane-${index}`}
                      className={`${styles.lane} ${index === activeLaneIndex ? styles.focusedLane : ""}`}
                      style={{ width: laneWidths[index] || DEFAULT_WIDTHS[index] || 340 }}
                    >
                      <header className={styles.laneHeader}>
                        <div className={styles.laneTopLine}>
                          <span>{lane.title}</span>
                          <span className={styles.widthPill}>{Math.round(laneWidths[index] || DEFAULT_WIDTHS[index] || 340)}px</span>
                        </div>
                        <div className={styles.laneSubtitle}>{lane.subtitle}</div>
                      </header>
                      <div className={styles.laneBody}>
                        {lane.comments.filter(visibleMatch).length === 0 ? (
                          <div className={styles.emptyLane}>No comments match this lane/search.</div>
                        ) : (
                          lane.comments.filter(visibleMatch).map((comment) => {
                            const isSelected = lane.selectedId === comment.id;
                            const isExpanded = expandedIds.has(comment.id) || isSelected;
                            return (
                              <article
                                key={comment.id}
                                className={`${styles.commentCard} ${isSelected ? styles.selectedComment : ""} ${getScopeClass(comment, latestEdit?.id)}`}
                                onClick={() => selectComment(comment, index)}
                              >
                                <div className={styles.commentMeta}>
                                  <span>{getScopeLabel(comment, latestEdit?.id)}</span>
                                  <span>{formatAge(comment.createdAt)}</span>
                                </div>
                                <div className={styles.author}>{comment.user?.name || "Unknown user"}</div>
                                <p className={isExpanded ? styles.commentTextExpanded : styles.commentTextCollapsed}>
                                  {stripHtml(comment.comment)}
                                </p>
                                <div className={styles.commentStats}>
                                  <span>{comment.children.length} direct</span>
                                  <span>{countDescendants(comment)} total</span>
                                  {comment.children.length > 0 && <span>{comment.children.length === 1 ? "single branch" : `${comment.children.length} branches`}</span>}
                                </div>
                                <div className={styles.commentActions}>
                                  <button type="button" onClick={(event) => { event.stopPropagation(); selectComment(comment, index); }}>
                                    {isExpanded ? "Collapse" : "Open"}
                                  </button>
                                  <button type="button" onClick={(event) => { event.stopPropagation(); selectComment(comment, index); setLaneWidthAt(index, maxLaneWidth()); window.setTimeout(() => focusLane(index), 0); }}>
                                    Fit lane
                                  </button>
                                </div>
                              </article>
                            );
                          })
                        )}
                      </div>
                    </section>
                    {index < lanes.length - 1 && (
                      <button
                        type="button"
                        className={`${styles.resizer} ${resizeState?.laneIndex === index ? styles.resizing : ""}`}
                        aria-label={`Resize lane ${index}`}
                        onMouseDown={(event) => handleStartResize(index, event)}
                        onDoubleClick={() => setLaneWidthAt(index, maxLaneWidth())}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className={styles.selectedSummary}>
              <strong>Selected comment:</strong>{" "}
              {selectedComment ? excerpt(selectedComment.comment, 220) : "Click a comment to build a path through the lanes."}
            </div>
          </section>
        )}

        {selectedGist && !loading && labMode === "cards" && (
          <section className={styles.labPanel} aria-label="Gist card experiment">
            <div className={styles.panelHeader}>
              <div>
                <div className={styles.eyebrow}>Experiment B</div>
                <h2>Compact Gist Cards</h2>
                <p>This is a deliberately isolated presentation experiment. It does not affect production cards.</p>
              </div>
            </div>
            <div className={styles.cardGrid}>
              {rankedGists.slice(0, 12).map((gist) => (
                <article key={gist.id} className={styles.gistCard}>
                  <div className={styles.cardKicker}>{getAllCommentsForGist(gist).length} comments</div>
                  <h3>{gist.title}</h3>
                  <p>{excerpt(getLatestEdit(gist)?.body, 180) || "No body preview available."}</p>
                  <button type="button" onClick={() => { setSelectedGistId(gist.id); setLabMode("lanes"); }}>
                    Open in Lanes Lab
                  </button>
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
};

export default DesignLabPage;
